import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, User } from 'lucide-react'
import { cn } from '@/lib/utils.ts'

export interface SchedulerSlot {
  /** ISO start timestamp (naive, without Z — Berlin wall-clock) */
  start: string
  /** ISO end timestamp */
  end: string
  /** Optional label (z. B. "Mo, 22.04. 09:00"); wird sonst aus start abgeleitet */
  label?: string
}

interface AppointmentSchedulerProps {
  slots: SchedulerSlot[]
  selectedSlotStart: string | null
  onSelect: (slot: SchedulerSlot) => void
  /** Optionaler Name des Zustaendigen (Techniker oder Abteilung) */
  technicianName?: string | null
  /** Art des Zustaendigen – steuert den Label-Prefix ("Techniker" / "Abteilung") */
  assigneeKind?: 'employee' | 'department' | null
  /** Optionaler Hinweis-Text unter dem Header */
  note?: string | null
  loading?: boolean
  /** Validierungs-Fehler (z. B. "Bitte einen Slot auswaehlen") */
  error?: string | null
}

/**
 * Kompakter Calendly-aehnlicher Termin-Picker.
 * - Links: Monatskalender mit klickbaren Tagen, an denen Slots verfuegbar sind.
 *   Kompakt: kleine runde Day-Buttons, nur Punkt-Marker fuer Verfuegbarkeit.
 * - Rechts: Vertikale Liste der verfuegbaren Slots fuer den ausgewaehlten Tag.
 */
export function AppointmentScheduler({
  slots,
  selectedSlotStart,
  onSelect,
  technicianName,
  assigneeKind,
  note,
  loading,
  error,
}: AppointmentSchedulerProps) {
  const assigneeLabel = assigneeKind === 'department' ? 'Abteilung' : 'Techniker'

  // ─── Slots nach Tag (YYYY-MM-DD lokal) gruppieren ────────────────────────
  const slotsByDay = useMemo(() => {
    const map = new Map<string, SchedulerSlot[]>()
    for (const s of slots) {
      const d = new Date(s.start)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const list = map.get(key) ?? []
      list.push(s)
      map.set(key, list)
    }
    return map
  }, [slots])

  const availableDays = useMemo(() => Array.from(slotsByDay.keys()).sort(), [slotsByDay])

  // ─── Aktiver Monat ──────────────────────────────────────────────────────
  const initialMonth = useMemo(() => {
    const ref = availableDays[0] ? new Date(`${availableDays[0]}T00:00:00`) : new Date()
    return new Date(ref.getFullYear(), ref.getMonth(), 1)
  }, [availableDays])
  const [viewMonth, setViewMonth] = useState<Date>(initialMonth)

  // ─── Aktiver Tag ─────────────────────────────────────────────────────────
  const [selectedDay, setSelectedDay] = useState<string | null>(null)

  // Beim Laden/Slot-Wechsel: wenn gewaehlter Tag nicht mehr verfuegbar oder noch
  // nichts gewaehlt → ersten verfuegbaren Tag nehmen.
  useEffect(() => {
    if (availableDays.length === 0) {
      setSelectedDay(null)
      return
    }
    if (selectedSlotStart) {
      const d = new Date(selectedSlotStart)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      if (slotsByDay.has(key)) {
        setSelectedDay(key)
        return
      }
    }
    setSelectedDay((prev) => (prev && slotsByDay.has(prev) ? prev : availableDays[0]!))
  }, [availableDays, selectedSlotStart, slotsByDay])

  const daySlots = selectedDay ? (slotsByDay.get(selectedDay) ?? []) : []

  return (
    <div>
    <div className={cn(
      'rounded-lg border bg-white overflow-hidden',
      error ? 'border-red-400' : 'border-gray-200',
    )}>
      {(technicianName || note) && (
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 text-sm">
          {technicianName ? (
            <div className="flex items-center gap-2 text-gray-800">
              <User className="w-4 h-4 text-gray-500" />
              {assigneeLabel}: <strong>{technicianName}</strong>
            </div>
          ) : null}
          {note ? <div className="text-xs text-gray-500 mt-0.5">{note}</div> : null}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] divide-gray-200">
        {/* Kalender (kompakt) -------------------------------------------- */}
        <div className="p-3 border-b md:border-b-0 md:border-r border-gray-200 md:w-[280px]">
          <MonthCalendar
            month={viewMonth}
            onChangeMonth={setViewMonth}
            availableDays={new Set(availableDays)}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
          />
        </div>

        {/* Slots ---------------------------------------------------------- */}
        <div className="p-3 flex flex-col min-w-0">
          {selectedDay ? (
            <div className="text-sm font-medium text-gray-900 mb-2">
              {formatDayHeading(selectedDay)}
            </div>
          ) : null}

          {loading ? (
            <div className="text-sm text-gray-500 py-6 text-center">Lade Slots …</div>
          ) : daySlots.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">
              {availableDays.length === 0
                ? 'Aktuell sind keine Slots verfügbar.'
                : 'Keine Slots an diesem Tag – bitte einen anderen Tag wählen.'}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-[340px] overflow-y-auto pr-1">
              {daySlots.map((s) => {
                const isSelected = selectedSlotStart === s.start
                return (
                  <button
                    key={s.start}
                    type="button"
                    onClick={() => onSelect(s)}
                    className={cn(
                      'w-full rounded-md border px-3 py-2 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                      isSelected
                        ? 'bg-brand-700 border-brand-700 text-white'
                        : 'bg-white border-gray-300 text-gray-800 hover:border-brand-500 hover:text-brand-700',
                    )}
                  >
                    {formatTimeOnly(s.start)}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    {error ? <div className="mt-1 text-caption text-red-600">{error}</div> : null}
    </div>
  )
}

// ─── Kompaktes Mini-Kalender ────────────────────────────────────────────────

interface MonthCalendarProps {
  month: Date
  onChangeMonth: (m: Date) => void
  availableDays: Set<string>
  selectedDay: string | null
  onSelectDay: (day: string) => void
}

function MonthCalendar({
  month,
  onChangeMonth,
  availableDays,
  selectedDay,
  onSelectDay,
}: MonthCalendarProps) {
  const weeks = useMemo(() => buildMonthGrid(month), [month])
  const keyRef = useRef(0)
  const today = new Date()
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-medium text-gray-900">
          {month.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            aria-label="Vorheriger Monat"
            onClick={() => onChangeMonth(addMonths(month, -1))}
            className="p-1 rounded hover:bg-gray-100 text-gray-600"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Nächster Monat"
            onClick={() => onChangeMonth(addMonths(month, 1))}
            className="p-1 rounded hover:bg-gray-100 text-gray-600"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0.5 text-[10px] uppercase tracking-wide text-gray-500 mb-1">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((d) => (
          <div key={d} className="text-center">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {weeks.map((cell) => {
          if (!cell) {
            keyRef.current += 1
            return <div key={`empty-${keyRef.current}`} className="h-8" />
          }
          const key = `${cell.getFullYear()}-${String(cell.getMonth() + 1).padStart(2, '0')}-${String(cell.getDate()).padStart(2, '0')}`
          const isAvailable = availableDays.has(key)
          const isSelected = selectedDay === key
          const isToday = todayKey === key
          const isOtherMonth = cell.getMonth() !== month.getMonth()
          return (
            <button
              key={key}
              type="button"
              disabled={!isAvailable}
              onClick={() => onSelectDay(key)}
              className={cn(
                'h-8 w-8 mx-auto rounded-full text-xs flex items-center justify-center transition',
                isSelected
                  ? 'bg-brand-700 text-white font-semibold'
                  : isAvailable
                    ? 'text-gray-900 font-medium hover:bg-brand-50 cursor-pointer'
                    : 'text-gray-300 cursor-not-allowed',
                isOtherMonth && !isAvailable && 'opacity-40',
                isToday && !isSelected && 'ring-1 ring-brand-400',
              )}
            >
              {cell.getDate()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildMonthGrid(month: Date): Array<Date | null> {
  const first = new Date(month.getFullYear(), month.getMonth(), 1)
  const last = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  // Mo=0 … So=6
  const startOffset = (first.getDay() + 6) % 7
  const cells: Array<Date | null> = []
  for (let i = 0; i < startOffset; i++) cells.push(null)
  for (let d = 1; d <= last.getDate(); d++) {
    cells.push(new Date(month.getFullYear(), month.getMonth(), d))
  }
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, 1)
}

function formatDayHeading(dayKey: string): string {
  const d = new Date(`${dayKey}T00:00:00`)
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  })
}

function formatTimeOnly(iso: string): string {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}
